import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { devWarn } from "../lib/log";

const EMPTY = { items: [], total: 0, next_cursor: null };

// Keyset-paginated duck list (GET <path>?limit&cursor&q). `cacheKey` (localStorage) makes the first page show
// instantly and revalidate in the background; `refresh()` re-fetches as many rows as are loaded (max 50 per call)
// so pages the user already opened are never discarded.
export const usePagedDucks = ({ path, pageSize = 10, q = "", cacheKey = null, enabled = true }) => {
  const [state, setState] = useState(() => {
    if (!cacheKey) return EMPTY;
    try {
      return JSON.parse(localStorage.getItem(cacheKey)) || EMPTY;
    } catch {
      return EMPTY;
    }
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const stateRef = useRef(state);
  stateRef.current = state;

  const persist = useCallback(
    (next) => {
      setState(next);
      if (cacheKey) {
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ ...next, items: next.items.slice(0, pageSize) }));
        } catch {}
      }
    },
    [cacheKey, pageSize],
  );

  const fetchPage = useCallback(
    async (limit, cursor) => {
      const { data } = await api.get(path, { params: { limit, ...(cursor ? { cursor } : {}), ...(q ? { q } : {}) } });
      return data;
    },
    [path, q],
  );

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const loaded = stateRef.current.items.length;
      let next = await fetchPage(Math.min(50, Math.max(pageSize, loaded)), null);
      while (next.items.length < loaded && next.next_cursor) {
        const more = await fetchPage(Math.min(50, loaded - next.items.length), next.next_cursor);
        next = { ...more, items: [...next.items, ...more.items] };
      }
      persist(next);
    } catch (e) {
      devWarn("Duck list refresh failed", e?.message);
    } finally {
      setLoading(false);
    }
  }, [enabled, fetchPage, pageSize, persist]);

  const loadMore = useCallback(async () => {
    const cur = stateRef.current;
    if (!cur.next_cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const more = await fetchPage(pageSize, cur.next_cursor);
      const seen = new Set(cur.items.map((c) => c.id));
      persist({ ...more, items: [...cur.items, ...more.items.filter((c) => !seen.has(c.id))] });
    } catch (e) {
      devWarn("Duck list load-more failed", e?.message);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, loadingMore, pageSize, persist]);

  const patchItem = useCallback(
    (id, fields) => persist({ ...stateRef.current, items: stateRef.current.items.map((c) => (c.id === id ? { ...c, ...fields } : c)) }),
    [persist],
  );

  const first = useRef(true);
  useEffect(() => {
    if (!enabled) return;
    if (!first.current) {
      stateRef.current = EMPTY; // a new query/path starts from the first page
      setState(EMPTY);
    }
    first.current = false;
    refresh();
  }, [enabled, refresh]);

  return { items: state.items, total: state.total, hasMore: !!state.next_cursor, loading, loadingMore, refresh, loadMore, patchItem };
};
